import { useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable image uploader.
 * Uploads a file to /api/uploads and returns the absolute URL via onChange.
 * `value` is expected to be a URL (either absolute or /api/uploads/... relative).
 *
 * Props:
 *   value            current image url string
 *   onChange(url)    called with new url after upload / cleared with ""
 *   recommendedSize  string, shown as help text  (e.g. "800 × 800")
 *   aspect           tailwind aspect utility for preview  (default "aspect-square")
 *   maxMB            informational only  (default 8)
 *   testId           prefix for data-testid on the input/button
 */
export default function ImageUpload({
  value,
  onChange,
  recommendedSize = "800 × 800",
  aspect = "aspect-square",
  maxMB = 8,
  testId = "image",
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const displayUrl = value?.startsWith("http") ? value : value ? `${BACKEND_URL}${value}` : "";

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxMB * 1024 * 1024) {
      toast.error(`File must be under ${maxMB}MB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className={`relative ${aspect} w-full bg-[#0B0E14] border border-dashed border-[#D4AF37]/30 rounded overflow-hidden flex items-center justify-center`}>
        {displayUrl ? (
          <img src={displayUrl} alt="preview" className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <div className="text-center px-4">
            <ImageIcon className="w-10 h-10 text-[#D4AF37]/40 mx-auto mb-1" strokeWidth={1.5} />
            <div className="text-xs text-[#FAF9F6]/50">No image yet</div>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
          </div>
        )}
        {displayUrl && !uploading && (
          <button
            type="button"
            data-testid={`${testId}-remove`}
            onClick={() => onChange("")}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-[#D4AF37] hover:bg-black flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testId}-upload-btn`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-[#050505] font-semibold text-xs px-3 py-2 rounded hover:bg-[#E8D08D] disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Uploading..." : displayUrl ? "Replace image" : "Upload image"}
        </button>
        <input
          ref={inputRef}
          data-testid={`${testId}-file-input`}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={handle}
        />
      </div>
      <div className="text-[10px] text-[#FAF9F6]/50 leading-tight">
        <div>Recommended: <span className="text-[#D4AF37]">{recommendedSize}</span> px · PNG, JPG, WEBP · max {maxMB}MB</div>
      </div>
    </div>
  );
}
